// ============================================
// VERIFICATIONS MANAGEMENT PAGE
// i18n: Faz 6D (v2.6)
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Check,
  X,
  ShieldCheck,
  Building2,
  User,
  FileText,
  Calendar,
  ExternalLink,
  Eye,
} from 'lucide-react';
import type { Verification, VerificationType, VerificationStatus } from '@crew-at-sea/shared-types';
import {
  PageHeader,
  FilterBar,
  FilterSelect,
  StatusBadge,
  ConfirmDialog,
  EmptyState,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, formatRelativeTime, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/verifications/')({
  component: VerificationsPage,
});

function VerificationsPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    verification: Verification | null;
    action: 'approve' | 'reject';
    note: string;
  }>({ open: false, verification: null, action: 'approve', note: '' });

  // Doğrulama taleplerini getir
  const { data: verificationsData, isLoading } = useQuery({
    queryKey: ['admin', 'verifications', typeFilter, statusFilter],
    queryFn: () => adminApi.getVerifications({
      type: typeFilter || undefined,
      status: statusFilter || undefined,
    }),
  });

  const typeIcons: Record<VerificationType, typeof User> = {
    IDENTITY: User,
    CERTIFICATE: FileText,
    COMPANY: Building2,
  };

  const typeColors: Record<VerificationType, string> = {
    IDENTITY: 'bg-blue-100 text-blue-700',
    CERTIFICATE: 'bg-amber-100 text-amber-700',
    COMPANY: 'bg-purple-100 text-purple-700',
  };

  // Onaylama mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveVerification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'verifications'] });
      setConfirmDialog({ open: false, verification: null, action: 'approve', note: '' });
    },
  });

  // Reddetme mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => adminApi.rejectVerification(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'verifications'] });
      setConfirmDialog({ open: false, verification: null, action: 'approve', note: '' });
    },
  });

  const handleAction = () => {
    if (!confirmDialog.verification) return;
    if (confirmDialog.action === 'approve') {
      approveMutation.mutate(confirmDialog.verification.id);
    } else {
      rejectMutation.mutate({ id: confirmDialog.verification.id, note: confirmDialog.note });
    }
  };

  const typeOptions = [
    { value: 'IDENTITY', label: t('enums:verificationType.IDENTITY') },
    { value: 'CERTIFICATE', label: t('enums:verificationType.CERTIFICATE') },
    { value: 'COMPANY', label: t('enums:verificationType.COMPANY') },
  ];

  const statusOptions = [
    { value: 'PENDING', label: t('enums:verificationStatus.PENDING') },
    { value: 'IN_REVIEW', label: t('enums:verificationStatus.IN_REVIEW') },
    { value: 'APPROVED', label: t('enums:verificationStatus.APPROVED') },
    { value: 'REJECTED', label: t('enums:verificationStatus.REJECTED') },
  ];

  const verifications = Array.isArray(verificationsData) ? verificationsData : ((verificationsData as any)?.data || []);

  const activeFiltersCount = (typeFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:verifications.title')}
        description={t('panel:verifications.description')}
      />

      <FilterBar
        filters={
          <>
            <FilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={typeOptions}
              placeholder={t('panel:verifications.allTypes')}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder={t('panel:verifications.allStatuses')}
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setTypeFilter('');
          setStatusFilter('');
        }}
      />

      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-maritime border border-gray-200 p-6 animate-pulse"
            >
              <div className="h-6 w-1/3 bg-gray-200 rounded mb-4" />
              <div className="h-4 w-full bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      ) : verifications && verifications.length > 0 ? (
        <div className="grid gap-4">
          {verifications.map((verification: Verification & { company?: { name: string; contactEmail?: string }; seafarer?: { firstName: string; lastName: string; email?: string }; documentUrls?: string[] }) => {
            const TypeIcon = typeIcons[verification.type as VerificationType];
            const entityName = verification.company
              ? verification.company.name
              : `${verification.seafarer?.firstName} ${verification.seafarer?.lastName}`;
            const entityEmail = verification.company
              ? verification.company.contactEmail
              : verification.seafarer?.email;

            return (
              <div
                key={verification.id}
                className="bg-white rounded-maritime border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  {/* Sol taraf */}
                  <div className="flex items-start gap-4">
                    <div
                      className={cn(
                        'flex items-center justify-center w-12 h-12 rounded-maritime',
                        typeColors[verification.type as VerificationType]
                      )}
                    >
                      <TypeIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {entityName}
                        </h3>
                        <StatusBadge status={verification.status} size="sm" />
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{entityEmail}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            typeColors[verification.type as VerificationType]
                          )}
                        >
                          {t(`enums:verificationType.${verification.type}`)} {t('panel:verifications.verification')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatRelativeTime(verification.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Sağ taraf - Aksiyonlar */}
                  <div className="flex items-center gap-2">
                    {verification.status === 'PENDING' ||
                    verification.status === 'IN_REVIEW' ? (
                      <>
                        <button
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              verification,
                              action: 'reject',
                              note: '',
                            })
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-maritime transition-colors"
                        >
                          <X className="h-4 w-4" />
                          {t('panel:verifications.reject')}
                        </button>
                        <button
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              verification,
                              action: 'approve',
                              note: '',
                            })
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-maritime transition-colors"
                        >
                          <Check className="h-4 w-4" />
                          {t('panel:verifications.approve')}
                        </button>
                      </>
                    ) : (
                      <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-maritime transition-colors">
                        <Eye className="h-4 w-4" />
                        {t('common:detail')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Belgeler */}
                {verification.documentUrls && verification.documentUrls.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('panel:verifications.uploadedDocuments', { count: verification.documentUrls.length })}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {verification.documentUrls.map((url: string, index: number) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-maritime transition-colors"
                        >
                          <FileText className="h-4 w-4" />
                          {t('panel:verifications.document', { number: index + 1 })}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-maritime border border-gray-200">
          <EmptyState
            title={t('panel:verifications.noVerifications')}
            description={t('panel:verifications.noVerificationsDesc')}
            variant="filter"
          />
        </div>
      )}

      {/* Onay/Red Dialogu */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={
          confirmDialog.action === 'approve'
            ? t('panel:verifications.approveTitle')
            : t('panel:verifications.rejectTitle')
        }
        description={
          <div className="space-y-4">
            <p>
              {confirmDialog.action === 'approve'
                ? t('panel:verifications.approveDescription')
                : t('panel:verifications.rejectDescription')}
            </p>
            {confirmDialog.action === 'reject' && (
              <textarea
                value={confirmDialog.note}
                onChange={(e) =>
                  setConfirmDialog({ ...confirmDialog, note: e.target.value })
                }
                placeholder={t('panel:verifications.rejectReasonPlaceholder')}
                className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 resize-none"
              />
            )}
          </div>
        }
        variant={confirmDialog.action === 'approve' ? 'success' : 'danger'}
        confirmText={confirmDialog.action === 'approve' ? t('panel:verifications.approve') : t('panel:verifications.reject')}
        onConfirm={handleAction}
        loading={approveMutation.isPending || rejectMutation.isPending}
      />
    </div>
  );
}
